import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AlertCircle, Eye, EyeOff, Mail, Lock, KeyRound } from "lucide-react";
import { sendResetOtp, resetPassword } from "../apis/auth";
import AuthBrand from "../components/AuthBrand";
import { useBranding } from "../contexts/BrandingContext";

export default function ForgetPassword() {
  const navigate = useNavigate();
  const brand = useBranding();
  const location = useLocation();
  const initialEmail = location.state?.email || "";

  const [email, setEmail] = useState(initialEmail);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await sendResetOtp({ email });
      if (response.data.success) {
        toast.success("OTP sent to your email");
        setOtpSent(true);
      } else {
        setError(response.data.error || "Failed to send OTP");
        toast.error(response.data.error || "Failed to send OTP");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to send OTP";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    if (!newPassword || !confirmNewPassword) {
      toast.error("Please enter new password and confirm it");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await resetPassword({ email, otp, newPassword });
      if (response.data.success) {
        toast.success("Password reset successful. Please login.");
        navigate("/login");
      } else {
        setError(response.data.error || "Failed to reset password");
        toast.error(response.data.error || "Failed to reset password");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to reset password";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <AuthBrand />

        <h1>{otpSent ? "Set a new password" : "Reset password"}</h1>
        <p className="auth-sub">
          {otpSent
            ? "Enter the code from your email, then choose a new password."
            : "We’ll send a one-time code to your registered email."}
        </p>

        {error && (
          <div className="ping-banner red">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {!otpSent ? (
          <form onSubmit={sendOtp} className="auth-form">
            <div className="auth-field">
              <label htmlFor="reset-email">Email</label>
              <div className="auth-input-wrap">
                <Mail className="auth-ico" size={16} />
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <button type="submit" className="ping-btn ping-btn-green" disabled={loading}>
              {loading ? <span className="auth-spin" /> : <Mail size={16} />}
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="auth-form">
            <div className="auth-field">
              <label htmlFor="reset-otp">OTP</label>
              <div className="auth-input-wrap">
                <KeyRound className="auth-ico" size={16} />
                <input
                  id="reset-otp"
                  name="otp"
                  type="text"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="newPassword">New password</label>
              <div className="auth-input-wrap">
                <Lock className="auth-ico" size={16} />
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="confirmNewPassword">Confirm password</label>
              <div className="auth-input-wrap">
                <Lock className="auth-ico" size={16} />
                <input
                  id="confirmNewPassword"
                  name="confirmNewPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Repeat password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" className="ping-btn ping-btn-green" disabled={loading}>
              {loading ? <span className="auth-spin" /> : <Lock size={16} />}
              {loading ? "Saving…" : "Reset password"}
            </button>
          </form>
        )}

        <div className="auth-foot">
          Remembered it?{" "}
          <Link to="/login" className="auth-link">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
